import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { datadogRum } from '@datadog/browser-rum';
import { catchError, map, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ProfileDataService } from '../../shared/services/profile-data.service';
import { RoleService } from '../../shared/services/role.service';
import { NavService } from '../../shared/services/nav.service';
import { NonProdBannerService } from '../../shared/services/non-prod-banner';

/**
 * Route guard that allows access only when the authenticated user has
 * completed onboarding (`is_onboarding_completed === true`).
 *
 * If the API returns an error (e.g. user record not yet created) or the flag
 * is falsy, the user is redirected to `/onboarding` so they can finish setup.
 * MSAL authentication is handled separately by MsalGuard and is not affected.
 */
export const onboardingGuard: CanActivateFn = () => {
  const profileDataService = inject(ProfileDataService);
  const nonProdBanner = inject(NonProdBannerService);
  const roleService = inject(RoleService);
  const navService = inject(NavService);
  const router = inject(Router);

  // When navigating from the onboarding completion flow the profile update may
  // not yet be visible to getUserDetails (caching / replication lag on server).
  // Trust the local completion signal and skip the API round-trip.
  const nav = router.getCurrentNavigation();
  if (nav?.extras?.state?.['onboardingJustCompleted']) {
    return true;
  }

  return profileDataService.getUserDetails().pipe(
    tap((data) => {
      roleService.setCurrentRole(data?.me_role);
      navService.setNavigation(data?.navigation ?? []);
      roleService.setDepartment(data?.department);
      nonProdBanner.setDisplayNonProdBanner(!data?.is_env_warning_dismissed);

      const department = (data?.department ?? '').toString().trim();
      if (environment.datadog?.enabled && department) {
        datadogRum.setUserProperty('department', department);
      }
    }),
    map((data) => {
      if (data?.is_onboarding_completed) {
        return true;
      }
      return router.createUrlTree(['/onboarding']);
    }),
    catchError(() => of(router.createUrlTree(['/onboarding']))),
  );
};
